import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, CheckCircle2, Target, ShieldCheck,
} from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import SEOHead from '@/components/marketing/SEOHead';
import PageHeader from '@/components/marketing/PageHeader';
import CTASection from '@/components/marketing/CTASection';
import DecorBackground from '@/components/marketing/DecorBackground';
import FAQAccordion from '@/components/marketing/FAQAccordion';
import { Button } from '@/components/ui/button';
import { LEAD_GEN_VERTICALS } from '@/data/lead-gen-content';
import { FORMATS } from '@/data/site-content';
import { breadcrumbJsonLd, serviceJsonLd, graph } from '@/lib/jsonld';

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};
const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
};

export default function LeadGenDetail() {
  const reduced = useReducedMotion();
  const { slug } = useParams();
  const v = LEAD_GEN_VERTICALS.find((x) => x.slug === slug);
  if (!v) return <Navigate to="/lead-generation" replace />;
  const Icon = v.icon;
  const stg = reduced ? {} : stagger;
  const up = reduced ? {} : fadeUp;
  const initial = reduced ? false : 'hidden';

  const recommendedFormats = (v.formats || [])
    .map((s) => FORMATS.find((f) => f.slug === s))
    .filter(Boolean);

  const others = LEAD_GEN_VERTICALS.filter((x) => x.slug !== v.slug);

  const faqJsonLd = Array.isArray(v.faq) && v.faq.length > 0
    ? {
      '@type': 'FAQPage',
      mainEntity: v.faq.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    }
    : null;

  const jsonLdNodes = [
    breadcrumbJsonLd([
      { label: 'Home', path: '/' },
      { label: 'Lead Generation', path: '/lead-generation' },
      { label: v.title },
    ]),
    serviceJsonLd({
      name: `${v.title} lead generation`,
      description: v.short,
      serviceType: 'Performance lead generation traffic',
      url: `/lead-generation/${v.slug}`,
    }),
  ];
  if (faqJsonLd) jsonLdNodes.push(faqJsonLd);

  return (
    <>
      <SEOHead path={`/lead-generation/${v.slug}`} jsonLd={graph(...jsonLdNodes)} />

      {/* Hero with image */}
      <section
        className="relative bg-[#050B17] text-white isolate overflow-hidden"
        data-testid={`leadgen-${v.slug}-hero`}
      >
        <div className="absolute inset-0">
          <img
            src={v.heroImage}
            alt={v.imageCaption}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg, rgba(5,11,23,0.55) 0%, rgba(5,11,23,0.85) 60%, rgba(5,11,23,1) 100%)',
            }}
          />
          <div
            aria-hidden="true"
            className="absolute inset-0 mix-blend-screen opacity-40"
            style={{
              background: `radial-gradient(circle at 78% 20%, ${v.accent}33, transparent 60%), radial-gradient(circle at 20% 80%, rgba(124,58,237,0.25), transparent 65%)`,
            }}
          />
        </div>
        <DecorBackground variant="hero" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-16 sm:pb-24">
          <nav className="text-[12.5px] font-medium text-slate-400 mb-5" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span className="mx-2 text-slate-600">/</span>
            <Link to="/lead-generation" className="hover:text-white transition-colors">Lead Generation</Link>
            <span className="mx-2 text-slate-600">/</span>
            <span className="text-slate-300">{v.title}</span>
          </nav>
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#7DDCFF] backdrop-blur-md"
                data-testid={`leadgen-${v.slug}-eyebrow`}
              >
                {v.eyebrow}
              </span>
              <h1 className="font-display mt-5 text-[34px] sm:text-[44px] lg:text-[52px] font-bold tracking-[-0.025em] leading-[1.05] text-white">
                Performance marketing for <span className="bg-gradient-to-r from-[#22D3EE] via-[#3B82F6] to-[#8B5CF6] bg-clip-text text-transparent">{v.title}</span>
              </h1>
              <p className="mt-5 text-[15.5px] sm:text-[16.5px] text-slate-300/90 leading-[1.7] max-w-2xl">
                {v.short}
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Button asChild data-testid={`leadgen-${v.slug}-apply`} className="btn-primary-gradient text-white rounded-xl px-6 py-5 text-[14px] font-semibold border-0">
                  <Link to="/apply" className="inline-flex items-center gap-2">Apply as Advertiser <ArrowRight className="h-4 w-4" /></Link>
                </Button>
                <Button asChild variant="outline" className="bg-white/[0.04] text-white border-white/15 hover:bg-white/10 hover:text-white rounded-xl px-5 py-5 text-[14px] font-semibold transition-colors backdrop-blur-md">
                  <Link to="/lead-generation" className="inline-flex items-center gap-2">All lead-gen verticals</Link>
                </Button>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div
                className="
                  relative rounded-2xl overflow-hidden h-[280px]
                  border border-[#7DDCFF]/25
                  backdrop-blur-xl
                  shadow-[0_30px_80px_rgba(0,0,0,0.55)]
                "
                aria-hidden="true"
              >
                <img
                  src={v.heroImage}
                  alt={v.imageCaption}
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      'linear-gradient(180deg, rgba(5,11,23,0.25) 0%, rgba(5,11,23,0.5) 100%)',
                  }}
                />
                <div
                  className="absolute top-0 left-0 right-0 h-px"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent, rgba(34,211,238,0.65), rgba(124,58,237,0.65), transparent)',
                  }}
                />
                <span
                  className="absolute top-4 left-4 inline-flex h-11 w-11 items-center justify-center rounded-xl border backdrop-blur-md"
                  style={{
                    background: `linear-gradient(180deg, ${v.accent}33, ${v.accent}10)`,
                    borderColor: `${v.accent}66`,
                    color: v.accent,
                    boxShadow: `0 0 22px ${v.accent}55`,
                  }}
                >
                  <Icon className="h-5 w-5" />
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Summary + Examples */}
      <section className="relative bg-[#06101F] text-white isolate overflow-hidden">
        <DecorBackground variant="section" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 prose-dashbound prose-invert-dashbound">
            <h2 className="font-display text-[26px] sm:text-[32px] font-bold tracking-[-0.02em] text-white">
              Why this vertical
            </h2>
            <p className="mt-4 text-[15.5px] text-slate-300/90 leading-[1.7]">
              {v.summary}
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-2xl bg-gradient-to-b from-white/[0.05] to-white/[0.02] border border-white/10 backdrop-blur-xl p-6">
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-[#7DDCFF]">
                Where it fits
              </span>
              <h3 className="mt-2 font-display text-[18px] font-semibold text-white">
                Common {v.title.toLowerCase()} use cases
              </h3>
              <ul className="mt-4 space-y-2.5" data-testid={`leadgen-${v.slug}-examples`}>
                {v.examples.map((ex) => (
                  <li key={ex} className="flex items-start gap-2.5 text-[13.5px] text-slate-200/90">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 text-[#34D399] shrink-0" />
                    <span>{ex}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How performance marketing works in this vertical */}
      <section className="relative bg-[#050B17] text-white isolate overflow-hidden" data-testid={`leadgen-${v.slug}-how`}>
        <DecorBackground variant="section" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#22D3EE] backdrop-blur-md">
              How it works
            </span>
            <h2 className="font-display mt-4 text-[26px] sm:text-[32px] font-bold tracking-[-0.02em] text-white">
              How performance marketing works in {v.title}
            </h2>
          </div>
          <motion.div
            variants={stg}
            initial={initial}
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
            className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
          >
            {v.howItWorks.map((step, i) => (
              <motion.div
                key={step.title}
                variants={up}
                className="
                  rounded-2xl
                  bg-gradient-to-b from-white/[0.05] to-white/[0.02]
                  border border-white/10
                  backdrop-blur-xl
                  p-5 h-full
                  transition-colors hover:border-[#7DDCFF]/30
                "
              >
                <span className="font-mono text-[12px] text-[#22D3EE]">0{i + 1}</span>
                <h3 className="mt-3 font-display text-[15.5px] font-semibold text-white">{step.title}</h3>
                <p className="mt-2 text-[13.5px] text-slate-300/90 leading-[1.7]">{step.body}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Recommended formats + KPI hints + Compliance */}
      <section className="relative bg-[#06101F] text-white isolate overflow-hidden">
        <DecorBackground variant="section" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 grid lg:grid-cols-12 gap-8">
          {/* Recommended formats */}
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#A78BFA] backdrop-blur-md">
              Recommended formats
            </span>
            <h2 className="font-display mt-4 text-[24px] sm:text-[28px] font-bold tracking-[-0.02em] text-white">
              Best traffic formats for {v.title}
            </h2>
            <ul className="mt-7 grid sm:grid-cols-2 gap-3" data-testid={`leadgen-${v.slug}-formats`}>
              {recommendedFormats.map((f) => (
                <li key={f.slug}>
                  <Link
                    to={`/ad-formats/${f.slug}`}
                    className="
                      group flex items-start gap-3 rounded-2xl
                      bg-gradient-to-b from-white/[0.05] to-white/[0.02]
                      border border-white/10 backdrop-blur-md
                      p-4 hover:border-[#7DDCFF]/40 transition-colors
                    "
                  >
                    <Target className="h-4 w-4 mt-0.5 text-[#22D3EE] shrink-0" />
                    <span>
                      <span className="block text-[14px] font-semibold text-white group-hover:text-[#7DDCFF] transition-colors">{f.title}</span>
                      <span className="block text-[12.5px] text-slate-400 mt-0.5">{f.short}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* KPI hints */}
          <div className="lg:col-span-5 space-y-5">
            <div className="rounded-2xl bg-gradient-to-b from-white/[0.05] to-white/[0.02] border border-white/10 backdrop-blur-xl p-6">
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-[#7DDCFF]">
                KPIs worth tracking
              </span>
              <h3 className="mt-2 font-display text-[17px] font-semibold text-white">Watch these metrics</h3>
              <ul className="mt-4 space-y-2.5">
                {v.kpiHints.map((k) => (
                  <li key={k} className="flex items-start gap-2.5 text-[13px] text-slate-200/90">
                    <ArrowRight className="h-3.5 w-3.5 mt-1 text-[#22D3EE] shrink-0" />
                    <span>{k}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl bg-[#0B172A]/80 border border-white/10 backdrop-blur-xl p-6">
              <div className="flex items-start gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#34D399]/15 border border-[#34D399]/30 text-[#34D399]">
                  <ShieldCheck className="h-4 w-4" />
                </span>
                <div className="flex-1 min-w-0">
                  <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                    Compliance notes
                  </span>
                  <ul className="mt-3 space-y-2 text-[12.5px] text-slate-300/90 leading-relaxed">
                    {v.compliance.map((c) => (
                      <li key={c} className="flex items-start gap-2">
                        <span className="text-[#34D399] mt-1">•</span>
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {Array.isArray(v.faq) && v.faq.length > 0 && (
        <section className="relative bg-[#050B17] text-white isolate overflow-hidden" data-testid={`leadgen-${v.slug}-faq`}>
          <DecorBackground variant="section" />
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#22D3EE] backdrop-blur-md">
              FAQ
            </span>
            <h2 className="font-display mt-4 text-[26px] sm:text-[32px] font-bold tracking-[-0.02em] text-white">
              {v.title} — frequently asked questions
            </h2>
            <div className="mt-7 rounded-2xl bg-gradient-to-b from-white/[0.05] to-white/[0.02] border border-white/10 backdrop-blur-xl px-5 sm:px-6 shadow-[0_24px_60px_rgba(0,0,0,0.45)]">
              <FAQAccordion items={v.faq} idPrefix={`leadgen-${v.slug}-faq`} dark />
            </div>
          </div>
        </section>
      )}

      {/* Other lead-gen verticals */}
      {others.length > 0 && (
        <section className="relative bg-[#06101F] text-white isolate overflow-hidden">
          <DecorBackground variant="section" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <h2 className="font-display text-[22px] sm:text-[26px] font-bold tracking-[-0.02em] text-white">
              Other lead-generation verticals
            </h2>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {others.map((o) => {
                const OIcon = o.icon;
                return (
                  <Link
                    key={o.slug}
                    to={`/lead-generation/${o.slug}`}
                    data-testid={`leadgen-${v.slug}-related-${o.slug}`}
                    className="
                      group flex items-start gap-4 rounded-2xl
                      bg-gradient-to-b from-white/[0.05] to-white/[0.02]
                      border border-white/10 backdrop-blur-xl p-5
                      transition-colors hover:border-[#7DDCFF]/35
                    "
                  >
                    <span
                      className="inline-flex h-10 w-10 items-center justify-center rounded-xl border shrink-0"
                      style={{
                        background: `linear-gradient(180deg, ${o.accent}26, ${o.accent}10)`,
                        borderColor: `${o.accent}55`,
                        color: o.accent,
                      }}
                    >
                      <OIcon className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <h3 className="font-display text-[15.5px] font-semibold text-white group-hover:text-[#7DDCFF] transition-colors">{o.title}</h3>
                      <p className="mt-1 text-[12.5px] text-slate-400 leading-relaxed line-clamp-2">{o.short}</p>
                      <span className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-[#22D3EE]">
                        Explore <ArrowUpRight className="h-3 w-3" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <CTASection primaryTo="/apply" />
    </>
  );
}
